import { loadGovernedBiContext, type BiRpcClient } from "@/lib/governedBiRetrieval";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { edgeColumns } from "@/../supabase/functions/_shared/edgeColumns";
import {
  fetchSuppressedContinuitySourceRowIds,
  formatPostgrestTextInList,
} from "@/lib/behavior/coachContinuityReviewService";
import {
  buildCoachingRecordSummaryFromCount,
  buildLeadershipRecordSummaryFromCount,
  type ContinuityRecordKind,
} from "@/lib/behavior/continuityRecord";
import {
  buildContinuityRecordExperience,
  type ContinuityRecordExperience,
  type ContinuityRecordMomentInput,
  type ContinuityRecordSignalInput,
  type ContinuityRecordTextInput,
} from "@/lib/behavior/viewModels/buildContinuityRecordExperience";
import type {
  LeadershipRecordItem,
  LeadershipRecordSourceType,
  LeadershipRecordVisibility,
} from "@/lib/behavior/viewModels/buildLeadershipRecordItems";

type SupabaseLike = {
  from: (table: string) => QueryLike;
};

type QueryLike = {
  select: (...args: unknown[]) => QueryLike;
  eq: (column: string, value: unknown) => QueryLike;
  in: (column: string, values: string[]) => QueryLike;
  not: (column: string, operator: string, value: string) => QueryLike;
  order: (...args: unknown[]) => QueryLike;
  limit: (count: number) => QueryLike;
  maybeSingle?: () => Promise<QueryResult<unknown>>;
  then: (
    onfulfilled: (value: QueryResult<unknown>) => unknown,
    onrejected?: (reason: unknown) => unknown,
  ) => Promise<unknown>;
};

type QueryResult<T> = {
  data: T | null;
  error: unknown;
};

type ContinuityMomentRow = {
  id: string;
  kind: ContinuityRecordKind;
  source: string;
  source_table?: string | null;
  source_id?: string | null;
  relationship_leader_id?: string | null;
  occurred_at?: string | null;
  metadata?: Record<string, unknown> | null;
};

type FetchContinuityRecordExperienceParams = {
  kind: ContinuityRecordKind;
  leaderId?: string | null;
  coachId?: string | null;
  relationshipLeaderId?: string | null;
  viewerId?: string | null;
};

function isMissingContinuityMomentsError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { code?: string; message?: string };
  return candidate.code === "42P01"
    || candidate.code === "PGRST205";
}

export class ContinuityRecordLoadError extends Error {
  readonly reasonCode = "continuity_record_query_failed";
  constructor() {
    super("The continuity record could not be loaded. Please try again.");
    this.name = "ContinuityRecordLoadError";
  }
}

function continuityReadError(error: unknown): ContinuityRecordLoadError {
  const code = error && typeof error === "object" && "code" in error ? error.code : undefined;
  console.error("[continuity-record] continuity_record_query_failed", {
    code: typeof code === "string" && /^[A-Z0-9]{5}$/.test(code) ? code : "unknown",
  });
  return new ContinuityRecordLoadError();
}

function client(): SupabaseLike {
  return supabase as unknown as SupabaseLike;
}

async function runListQuery<T>(query: QueryLike): Promise<T[]> {
  const result = await query as QueryResult<T[]>;
  if (result.error) throw continuityReadError(result.error);
  return Array.isArray(result.data) ? result.data : [];
}

async function runMaybeSingle<T>(query: QueryLike): Promise<T | null> {
  if (typeof query.maybeSingle === "function") {
    const result = await query.maybeSingle() as QueryResult<T>;
    if (result.error) throw continuityReadError(result.error);
    return result.data ?? null;
  }
  const result = await query as QueryResult<T>;
  if (result.error) throw continuityReadError(result.error);
  return result.data ?? null;
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function evidenceTitle(row: ContinuityMomentRow): string | null {
  const metadata = row.metadata || {};
  return asText(metadata.title) || asText(metadata.meeting_title) || asText(metadata.label);
}

function evidenceExcerpt(row: ContinuityMomentRow): string | null {
  const metadata = row.metadata || {};
  return asText(metadata.excerpt) || asText(metadata.summary) || asText(metadata.reflection_text);
}

function toMomentInput(row: ContinuityMomentRow): ContinuityRecordMomentInput {
  return {
    id: row.id,
    source: row.source,
    sourceTable: row.source_table,
    sourceId: row.source_id,
    occurredAt: row.occurred_at,
    title: evidenceTitle(row),
    excerpt: evidenceExcerpt(row),
  };
}

function toLeadershipRecordSourceType(row: ContinuityMomentRow): LeadershipRecordSourceType {
  if (row.source_table === "transcript_uploads") return "transcript_evidence";
  if (row.source_table === "post_meeting_reflections") return "reflection";
  if (row.source_table === "coach_leader_session_records") return "coach_note";
  if (row.source_table === "leader_memory_ledger") return "leader_memory_event";
  if (row.source_table === "practice_summaries") return "practice_summary";
  if (row.source.includes("context_graph")) return "context_graph_node";
  return row.kind === "coaching" ? "coach_note" : "leader_memory_event";
}

function toLeadershipRecordVisibility(sourceVisibleToLeader: boolean): LeadershipRecordVisibility {
  return sourceVisibleToLeader ? "leader_safe" : "coach_only";
}

function toLeadershipRecordItem(params: {
  row: ContinuityMomentRow;
  leaderId?: string | null;
  coachId?: string | null;
  sourceVisibleToLeader: boolean;
}): LeadershipRecordItem {
  const title = evidenceTitle(params.row) || params.row.source.replace(/[_-]+/g, " ");
  return {
    id: params.row.id,
    leaderId: params.leaderId || params.row.relationship_leader_id || params.coachId || "unknown",
    sourceType: toLeadershipRecordSourceType(params.row),
    sourceId: params.row.source_id || params.row.id,
    carryForwardSourceTable: "continuity_moments",
    carryForwardSourceRowId: params.row.id,
    title,
    observedFact: evidenceExcerpt(params.row) || title,
    occurredAt: params.row.occurred_at || undefined,
    createdAt: params.row.occurred_at || new Date(0).toISOString(),
    visibility: toLeadershipRecordVisibility(params.sourceVisibleToLeader),
    evidenceCount: 1,
  };
}

async function fetchContinuityMoments(params: FetchContinuityRecordExperienceParams): Promise<ContinuityMomentRow[]> {
  if (params.kind === "leadership" && !params.leaderId) return [];
  let suppressedSourceRowIds: string[] = [];
  if (params.kind === "leadership" && params.coachId && params.leaderId) {
    const { data, error } = await fetchSuppressedContinuitySourceRowIds({
      supabase,
      coachId: params.coachId,
      leaderId: params.leaderId,
      mode: "leader",
      sourceTable: "continuity_moments",
    });
    if (error) throw continuityReadError(error);
    suppressedSourceRowIds = data ?? [];
  }

  let query = client()
    .from("continuity_moments")
    .select("id, kind, source, source_table, source_id, relationship_leader_id, occurred_at, metadata")
    .eq("kind", params.kind)
    .order("occurred_at", { ascending: false })
    .limit(100);

  if (params.kind === "leadership" && params.leaderId) {
    query = query.eq("leader_id", params.leaderId);
  }
  if (suppressedSourceRowIds.length > 0) {
    query = query.not("id", "in", formatPostgrestTextInList(suppressedSourceRowIds));
  }
  if (params.kind === "coaching" && params.coachId) {
    query = query.eq("coach_id", params.coachId);
  }
  if (params.kind === "coaching" && params.relationshipLeaderId) {
    query = query.eq("relationship_leader_id", params.relationshipLeaderId);
  }

  const result = await query as QueryResult<ContinuityMomentRow[]>;
  if (result.error) {
    const code = result.error && typeof result.error === "object" && "code" in result.error
      ? result.error.code : undefined;
    console.error("[continuity-record] continuity_record_query_failed", {
      code: typeof code === "string" && /^[A-Z0-9]{5}$/.test(code) ? code : "unknown",
    });
    if (isMissingContinuityMomentsError(result.error)) return [];
    throw new ContinuityRecordLoadError();
  }
  return Array.isArray(result.data) ? result.data : [];
}

async function fetchPublishedCoachingSourceIds(
  moments: ContinuityMomentRow[], params: FetchContinuityRecordExperienceParams,
): Promise<Set<string>> {
  const sourceIds = moments.filter(row => row.source_table === "coach_leader_session_records")
    .flatMap(row => row.source_id ? [row.source_id] : []);
  if (params.kind !== "coaching" || !params.coachId || sourceIds.length === 0) return new Set();
  let query = client().from("coach_leader_session_records")
    .select(edgeColumns("coach_leader_session_records", ["id", "coach_id", "leader_id", "published_to_leader"]))
    .eq("coach_id", params.coachId).eq("published_to_leader", true).in("id", sourceIds);
  if (params.relationshipLeaderId) query = query.eq("leader_id", params.relationshipLeaderId);
  const rows = await runListQuery<{ id: string; coach_id: string; leader_id: string; published_to_leader: boolean }>(query);
  // A historical moment or client metadata never attests current publication.
  return new Set(rows.filter(row => row.coach_id === params.coachId && row.published_to_leader === true
    && sourceIds.includes(row.id) && (!params.relationshipLeaderId || row.leader_id === params.relationshipLeaderId)
    && moments.some(moment => moment.source_id === row.id && moment.relationship_leader_id === row.leader_id))
    .map(row => row.id));
}

async function fetchRelationshipName(relationshipLeaderId?: string | null): Promise<string | null> {
  if (!relationshipLeaderId) return null;
  const row = await runMaybeSingle<{ full_name?: string | null }>(
    client().from("profiles").select("full_name").eq("id", relationshipLeaderId),
  );
  return asText(row?.full_name);
}

async function fetchLeaderBiSignals(leaderId: string | null | undefined, audience: "coach"|"leader") {
  if (!leaderId) return {themes:[],tensions:[]};
  const context = await loadGovernedBiContext(supabase as unknown as BiRpcClient,leaderId,audience);
  return {themes:context.themes.slice(0,6).map(row => ({label:row.theme_name,detail:audience === "coach" && row.supporting_meeting_count ? String(row.supporting_meeting_count)+" supporting meetings" : null})),
    tensions:audience === "coach" ? context.clusters.filter(row => row.contradiction_count > 0).slice(0,6).map(row => ({label:row.behavior_theme,detail:String(row.contradiction_count)+" unresolved tensions"})) : []};
}

async function fetchLeaderReflections(leaderId?: string | null): Promise<ContinuityRecordTextInput[]> {
  if (!leaderId) return [];
  const rows = await runListQuery<{
    id: string;
    reflection_text?: string | null;
    created_at?: string | null;
  }>(
    client()
      .from("post_meeting_reflections")
      .select("id, reflection_text, created_at")
      .eq("leader_id", leaderId)
      .order("created_at", { ascending: false })
      .limit(5),
  );
  return rows
    .map((row) => {
      const text = asText(row.reflection_text);
      if (!text) return null;
      return {
        id: row.id,
        text,
        occurredAt: row.created_at ?? null,
        href: `/post-meeting/${row.id}`,
      };
    })
    .filter((row): row is ContinuityRecordTextInput => Boolean(row));
}

async function fetchCoachingActivity(params: {
  coachId?: string | null;
  relationshipLeaderId?: string | null;
}): Promise<ContinuityRecordTextInput[]> {
  if (!params.coachId) return [];
  let query = client()
    .from("coach_leader_session_records")
    .select("id, coach_note, created_at")
    .eq("coach_id", params.coachId)
    .order("created_at", { ascending: false })
    .limit(5);
  if (params.relationshipLeaderId) {
    query = query.eq("leader_id", params.relationshipLeaderId);
  }
  const rows = await runListQuery<{
    id: string;
    coach_note?: string | null;
    created_at?: string | null;
  }>(query);
  return rows
    .map((row) => {
      const text = asText(row.coach_note);
      if (!text) return null;
      return {
        id: row.id,
        text,
        occurredAt: row.created_at ?? null,
        href: params.relationshipLeaderId
          ? `/coach/leaders/${params.relationshipLeaderId}?section=coach-growth&id=${row.id}`
          : "/coach/record",
      };
    })
    .filter((row): row is ContinuityRecordTextInput => Boolean(row));
}

async function fetchCoachLens(coachId?: string | null): Promise<{ title: string; body: string } | null> {
  if (!coachId) return null;
  const row = await runMaybeSingle<{
    coaching_philosophy?: string | null;
    frameworks?: string[] | string | null;
  }>(
    client()
      .from("coach_profiles")
      .select(edgeColumns("coach_profiles", ["coaching_philosophy", "frameworks"]))
      .eq("coach_id", coachId),
  );
  const body = asText(row?.coaching_philosophy);
  if (body) return { title: "Philosophy continuity", body };
  if (Array.isArray(row?.frameworks) && row.frameworks.length > 0) {
    return { title: "Philosophy continuity", body: row.frameworks.join(", ") };
  }
  if (typeof row?.frameworks === "string" && row.frameworks.trim()) {
    return { title: "Philosophy continuity", body: row.frameworks.trim() };
  }
  return null;
}

export async function fetchContinuityRecordExperience(
  params: FetchContinuityRecordExperienceParams,
): Promise<ContinuityRecordExperience> {
  const [moments, relationshipName] = await Promise.all([
    fetchContinuityMoments(params),
    fetchRelationshipName(params.relationshipLeaderId),
  ]);
  // Leadership rows already passed the relationship/source predicate in RLS;
  // a second client RPC would duplicate the boundary and add a deploy hazard.
  const publishedCoachingSourceIds = await fetchPublishedCoachingSourceIds(moments, params);
  const leadershipRecordItems = moments.map((row) =>
    toLeadershipRecordItem({
      row,
      leaderId: params.leaderId,
      coachId: params.coachId,
      sourceVisibleToLeader: row.kind === "leadership" || (row.source_table === "coach_leader_session_records"
        && Boolean(row.source_id && publishedCoachingSourceIds.has(row.source_id))),
    })
  );

  const summary = params.kind === "leadership"
    ? buildLeadershipRecordSummaryFromCount(moments.length)
    : buildCoachingRecordSummaryFromCount(moments.length, relationshipName);

  if (params.kind === "leadership") {
    const [bi, reflections] = await Promise.all([
      fetchLeaderBiSignals(params.leaderId,params.viewerId && params.viewerId !== params.leaderId ? "coach" : "leader"),
      fetchLeaderReflections(params.leaderId),
    ]);

    return buildContinuityRecordExperience({
      kind: "leadership",
      summary,
      moments: moments.map(toMomentInput),
      leadershipRecordItems,
      themes:bi.themes,
      tensions:bi.tensions,
      reflections,
    });
  }

  const supportingLeaderId = params.relationshipLeaderId ?? null;
  const [activity, bi, lens] = await Promise.all([
    fetchCoachingActivity({
      coachId: params.coachId,
      relationshipLeaderId: params.relationshipLeaderId,
    }),
    fetchLeaderBiSignals(supportingLeaderId,"coach"),
    fetchCoachLens(params.coachId),
  ]);

  return buildContinuityRecordExperience({
    kind: "coaching",
    summary,
    relationshipId: params.relationshipLeaderId,
    relationshipName,
    moments: moments.map(toMomentInput),
    leadershipRecordItems,
    themes:bi.themes,
    tensions:bi.tensions,
    coachingActivity: activity,
    lens,
  });
}

export function useLeadershipRecordExperience(
  leaderId?: string | null,
  coachId?: string | null,
) {
  const {user} = useAuth();
  const query = useQuery({
    queryKey: ["continuity-record-experience", "leadership", leaderId, coachId ?? null, user?.id],
    enabled: Boolean(leaderId && user?.id),
    staleTime:0,
    refetchOnMount:"always",
    queryFn: () => fetchContinuityRecordExperience({ kind: "leadership", leaderId, coachId, viewerId:user?.id }),
  });
  return {...query,data:query.isFetching || query.isError ? undefined : query.data,isLoading:query.isLoading || query.isFetching};
}

export function useCoachingRecordExperience(params: {
  coachId?: string | null;
  relationshipLeaderId?: string | null;
  enabled?: boolean;
  accessGeneration?: string | null;
  relationshipRevision?: string | null;
}) {
  const {user} = useAuth();
  const query = useQuery({
    queryKey: [
      "continuity-record-experience",
      "coaching",
      user?.id,
      params.coachId,
      params.relationshipLeaderId ?? "portfolio",
      params.accessGeneration ?? null,
      params.relationshipRevision ?? null,
    ],
    enabled: Boolean(params.enabled ?? true) && Boolean(params.coachId),
    staleTime:0,
    refetchOnMount:"always",
    queryFn: () => fetchContinuityRecordExperience({
      kind: "coaching",
      coachId: params.coachId,
      relationshipLeaderId: params.relationshipLeaderId,
    }),
  });
  return {...query,data:query.isFetching || query.isError ? undefined : query.data,isLoading:query.isLoading || query.isFetching};
}
